/**
 * Build a topical fallback image URL for a news article based on its title.
 * Uses loremflickr.com (free, no key required) which serves Creative-Commons
 * Flickr photos tagged with the given keywords. `lock` makes the pick
 * deterministic per article so users don't see it flicker between refreshes.
 */

// Keyword → search-tags mapping. Longest / most-specific matches first.
// Values are comma-separated tags loremflickr uses to filter.
const TOPIC_MAP = [
    // Corporate actions
    { re: /\bipo\b|initial public offering/i,           tags: "ipo,stockmarket,newyorkstockexchange" },
    { re: /buyback|share ?repurchase/i,                 tags: "money,cash,finance" },
    { re: /dividend|bonus issue|payout/i,               tags: "money,dividend,finance" },
    { re: /merger|acquisition|takeover|acquires/i,      tags: "handshake,business,corporate" },
    { re: /earnings|results|profit|revenue|q[1-4] fy/i, tags: "earnings,chart,finance" },
    { re: /loss(es)?|deficit|miss(ed)? estimates/i,     tags: "chart,decline,finance" },
    { re: /upgrade|downgrade|target price|analyst/i,    tags: "analyst,chart,stockmarket" },

    // Sectors / companies
    { re: /oil|crude|petroleum|opec|refiner/i,          tags: "oil,petroleum,industry" },
    { re: /gold|silver|precious metal|bullion/i,        tags: "gold,bullion,commodity" },
    { re: /gas\b|lng|natural gas/i,                     tags: "naturalgas,pipeline,industry" },
    { re: /steel|iron|metal/i,                          tags: "steel,factory,industry" },
    { re: /cement|construction|infra/i,                 tags: "construction,cement,building" },
    { re: /pharma|drug|medicine|hospital|healthcare/i,  tags: "pharmacy,laboratory,medicine" },
    { re: /auto|car|vehicle|ev\b|electric vehicle/i,    tags: "car,automobile,factory" },
    { re: /bank|banking|nbfc|lender|hdfc|icici|sbi/i,   tags: "bank,finance,building" },
    { re: /it services|infosys|tcs|wipro|technology|tech stock/i, tags: "technology,office,computer" },
    { re: /telecom|airtel|jio|vodafone|5g/i,            tags: "telecom,tower,mobilephone" },
    { re: /realty|realestate|property|housing/i,        tags: "realestate,building,skyline" },
    { re: /textile|apparel|garment/i,                   tags: "textile,factory,fashion" },
    { re: /aviation|airline|indigo|spicejet/i,          tags: "airplane,airport,aviation" },
    { re: /power|electricity|renewable|solar|wind energy/i, tags: "power,energy,solar" },
    { re: /agri|farmer|crop|monsoon|agri/i,             tags: "agriculture,farm,field" },
    { re: /fmcg|consumer goods|hul|itc|nestle/i,        tags: "supermarket,retail,shopping" },

    // Policy / macro
    { re: /rbi|repo rate|monetary policy|interest rate/i, tags: "reservebank,finance,architecture" },
    { re: /fed|federal reserve|fomc|jerome powell/i,    tags: "federalreserve,washington,dollar" },
    { re: /budget|fiscal|tax|gst/i,                     tags: "parliament,government,finance" },
    { re: /inflation|cpi|wpi/i,                         tags: "inflation,shopping,economy" },
    { re: /gdp|economy|growth/i,                        tags: "economy,city,skyline" },
    { re: /tariff|trade war|import|export/i,            tags: "shippingcontainer,trade,port" },
    { re: /rupee|dollar|forex|currency/i,               tags: "currency,money,exchange" },

    // Markets
    { re: /nifty|sensex|nse\b|bse\b/i,                  tags: "stockexchange,mumbai,trading" },
    { re: /nasdaq|dow jones|s&p|wall street/i,          tags: "wallstreet,newyork,stockmarket" },
    { re: /nikkei|japan/i,                              tags: "tokyo,japan,skyline" },
    { re: /hang seng|hong kong/i,                       tags: "hongkong,skyline,harbour" },
    { re: /kospi|korea/i,                               tags: "seoul,korea,city" },
    { re: /shanghai|china/i,                            tags: "shanghai,skyline,china" },
    { re: /dax|germany|frankfurt/i,                     tags: "frankfurt,germany,skyline" },
    { re: /ftse|london|uk\b|britain/i,                  tags: "london,skyline,uk" },

    // Bonds / debt
    { re: /bond|debt|yield|treasury/i,                  tags: "bond,finance,paper" },

    // Crypto
    { re: /bitcoin|crypto|ethereum|blockchain/i,        tags: "bitcoin,cryptocurrency,digital" },

    // Generic buckets
    { re: /market|stocks?|equity|shares?/i,             tags: "stockmarket,chart,trading" },
    { re: /investor|mutual fund|sip\b|hedge/i,          tags: "investment,portfolio,finance" },
];

const DEFAULT_TAGS = "stockmarket,finance,trading";

// Simple deterministic hash used as the ?lock= seed
function hash32(str) {
    let h = 5381;
    const s = String(str || "");
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}

export function tagsForTitle(title = "") {
    for (const { re, tags } of TOPIC_MAP) {
        if (re.test(title)) return tags;
    }
    return DEFAULT_TAGS;
}

/**
 * Returns a stable topical image URL for a news item.
 * Falls back to internal SVG if this URL fails to load (handled in NewsCard).
 */
export function topicImageUrl(item, width = 800, height = 500) {
    const tags = tagsForTitle(item?.title || "");
    const seed = hash32((item?.id || "") + "|" + (item?.title || "")) % 1000;
    return `https://loremflickr.com/${width}/${height}/${encodeURIComponent(tags)}?lock=${seed}`;
}
